/**
 * Notification reference utilities for parsing and rendering embedded references.
 *
 * Reference format: {{type:display:id}}
 *
 * Supported types:
 * - patient, provider, event, call, message, chat, form
 * - invoice, payment, charge, adjustment
 * - location, email, mail, review
 * - stock, supplier (inventory)
 * - teamconversation, socialconversation
 * - opportunity / lead (interchangeable; both open the leads board)
 * - lab (carries the patient id)
 * - approval (a settings change request)
 */

/**
 * Supported reference types
 */
export type ReferenceType =
  | "patient"
  | "provider"
  | "event"
  | "waitlist"
  | "call"
  | "message"
  | "chat"
  | "form"
  | "invoice"
  | "payment"
  | "charge"
  | "adjustment"
  | "location"
  | "email"
  | "mail"
  | "review"
  | "note"
  | "shift"
  | "stock"
  | "item"
  | "supplier"
  | "shipment"
  | "po"
  | "deposit"
  | "chargeback"
  | "cashbalance"
  | "coupa_drift"
  | "coupa_reconciliation"
  | "assistant"
  | "task"
  | "teamconversation"
  | "socialconversation"
  | "opportunity"
  | "lead"
  | "lab"
  | "approval";

/**
 * Parsed reference from notification content
 */
export interface Reference {
  type: ReferenceType;
  display: string;
  id: string;
}

/**
 * Segment of parsed notification content - either plain text or a reference
 */
export type ContentSegment =
  | { type: "text"; value: string }
  | { type: "reference"; reference: Reference };

// Regex pattern to match references: {{type:display:id}}
// Display text can contain escaped colons (\:) from backend escaping
const REFERENCE_PATTERN = /\{\{([a-z_]+):((?:\\.|[^:}])+):([^}]+)\}\}/;

/**
 * Strip the delimiter escapes the backend writes (`\:`, `\{`, `\}`, `\\`).
 *
 * Applies to free text as well as a token's display: `escape_text` escapes braces
 * in user-controlled content — a DM body, an SMS — so it can't forge a reference
 * token, and that text renders with visible backslashes unless it is unescaped
 * here too. Mirrors `_unescape_delimiters` in
 * decoda/common/notification_content.py; the two must agree.
 */
function unescapeDelimiters(text: string): string {
  return text.replace(/\\([:{}\\])/g, "$1");
}

/**
 * Parse notification content into segments of text and references.
 *
 * @param content - The content string containing embedded references
 * @param options.unescapeText - Unescape delimiters in the free-text segments.
 *   Defaults to true, which is right for display-only renderers. Pass false when
 *   the parsed text is loaded into an editor or otherwise round-tripped back to
 *   storage: unescaping there drops the backslashes, and the next save persists
 *   raw `{{…}}`, turning user-supplied text into a real reference.
 * @returns Array of content segments (text or reference)
 */
export function parseNotificationContent(
  content: string | undefined | null,
  options?: { unescapeText?: boolean },
): ContentSegment[] {
  if (!content) {
    return [];
  }

  const unescapeText = options?.unescapeText ?? true;
  const renderText = (value: string) =>
    unescapeText ? unescapeDelimiters(value) : value;

  const segments: ContentSegment[] = [];
  let lastIndex = 0;

  // Create a local regex with the g flag to avoid shared lastIndex state
  const pattern = new RegExp(REFERENCE_PATTERN.source, "g");

  let match;
  while ((match = pattern.exec(content)) !== null) {
    // Add text before this match
    if (match.index > lastIndex) {
      segments.push({
        type: "text",
        value: renderText(content.slice(lastIndex, match.index)),
      });
    }

    // Add the reference
    const [, refType, display, id] = match;
    const unescapedDisplay = unescapeDelimiters(display);

    segments.push({
      type: "reference",
      reference: {
        type: refType as ReferenceType,
        display: unescapedDisplay,
        id,
      },
    });

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text after last match
  if (lastIndex < content.length) {
    segments.push({
      type: "text",
      value: renderText(content.slice(lastIndex)),
    });
  }

  return segments;
}

/** First Whisper conversation reference in the alert content, if it carries one. */
export function teamConversationIdFrom(
  content: string | undefined | null,
): string | null {
  for (const segment of parseNotificationContent(content)) {
    if (
      segment.type === "reference" &&
      segment.reference.type === "teamconversation"
    ) {
      return segment.reference.id;
    }
  }
  return null;
}

/**
 * Get the route path for a reference type.
 *
 * @param type - The reference type
 * @param id - The reference ID
 * @returns Route path or null if no direct route exists
 */
export function getRouteForReference(
  type: ReferenceType,
  id: string,
  options?: { taskId?: string },
): string | null {
  switch (type) {
    case "patient":
      return `/patient/${id}`;
    case "provider":
      return null;
    case "event":
      return `/check-in/calendar?eventId=${id}`;
    case "waitlist":
      return `/check-in/appointment-form/new?waitlistId=${encodeURIComponent(id)}`;
    case "call": {
      const params = new URLSearchParams({ callId: id });
      if (options?.taskId) {
        params.set("taskId", options.taskId);
      }
      return `/comms/call?${params.toString()}`;
    }
    case "message":
    case "chat":
      return `/comms/chat?chatId=${id}`;
    case "form":
      // The id is the submission's.
      return `/check-in/forms/submissions?id=${id}`;
    case "invoice":
      return `/billing/invoices?id=${id}`;
    case "payment":
      return `/billing/payments?id=${id}`;
    case "charge":
      return `/billing/charges?id=${id}`;
    case "adjustment":
      return `/billing/charges?id=${id}`;
    case "location":
      return `/settings/locations?id=${id}`;
    case "email":
      return null;
    case "mail":
      return null;
    case "review":
      return null;
    case "note":
      return `/scribe/playground/${id}`;
    case "shift":
      return `/settings/shifts`;
    case "stock":
      return `/settings/stock`;
    case "item":
      return `/settings/products`;
    case "supplier":
      return null;
    case "shipment":
    case "po":
      return `/inventory/purchase-orders`;
    case "deposit":
      return `/billing/deposits`;
    case "cashbalance":
      return `/billing/cash-balances`;
    case "coupa_drift":
      return `/enterprise/coupa-drift`;
    case "coupa_reconciliation":
      return `/settings/coupa?tab=reconciliation`;
    case "chargeback":
      return `/billing/chargebacks?id=${id}`;
    case "assistant":
      return null;
    case "task":
      return `/tasks?taskId=${id}`;
    // Two spellings, one destination. The API emits `opportunity` (the model's
    // name); `lead` is accepted so a later backend flip to the UI's word cannot
    // produce a dead link, and so old stored content keeps resolving either way.
    case "opportunity":
    case "lead":
      return `/growth/leads?view=kanban&leadId=${id}`;
    case "teamconversation":
      return null; // Handled via drawer, not page navigation
    case "socialconversation":
      return `/comms/social?conversationId=${id}`;
    // The id is the PATIENT's — results live on a tab of the chart.
    case "lab":
      return `/patient/${id}?tab=labs`;
    case "approval":
      return `/settings/approvals?requestId=${id}`;
    default:
      return null;
  }
}

/**
 * List pages that take several ids at once (`?id=a&id=b`), by reference type,
 * with the plural noun a link to them reads as.
 */
const LIST_ROUTES: Partial<
  Record<ReferenceType, { path: string; noun: string }>
> = {
  payment: { path: "/billing/payments", noun: "payments" },
  charge: { path: "/billing/charges", noun: "charges" },
  form: { path: "/check-in/forms/submissions", noun: "forms" },
};

/**
 * Where a set of references of one type opens together, or null when that type
 * has no list page that takes ids.
 */
export function getListRouteForReferences(
  type: ReferenceType,
  ids: readonly string[],
): { route: string; noun: string } | null {
  const list = LIST_ROUTES[type];
  if (!list || ids.length === 0) return null;
  const params = new URLSearchParams();
  ids.forEach((id) => params.append("id", id));
  return { route: `${list.path}?${params.toString()}`, noun: list.noun };
}

/** "payments" — what a list link for this reference type reads as, if it has one. */
export function listNounForReference(type: ReferenceType): string | null {
  return LIST_ROUTES[type]?.noun ?? null;
}

/** The first reference type in this content that has a list page. */
export function listableReferenceType(
  content: string | undefined | null,
): ReferenceType | null {
  for (const segment of parseNotificationContent(content)) {
    if (segment.type === "reference" && LIST_ROUTES[segment.reference.type]) {
      return segment.reference.type;
    }
  }
  return null;
}

/** Every distinct id of this reference type across these contents, in order. */
export function referenceIdsOfType(
  contents: readonly (string | undefined | null)[],
  type: ReferenceType,
): string[] {
  const ids = new Set<string>();
  for (const content of contents) {
    for (const segment of parseNotificationContent(content)) {
      if (segment.type === "reference" && segment.reference.type === type) {
        ids.add(segment.reference.id);
      }
    }
  }
  return Array.from(ids);
}

/**
 * Escape a reference's display text so it round-trips through the
 * `{{type:display:id}}` format. Colons and braces are structural separators,
 * so any literal occurrence inside the display must be backslash-escaped.
 *
 * This is the exact inverse of the unescaping performed in
 * {@link parseNotificationContent}.
 */
function escapeReferenceDisplay(display: string): string {
  // Backslash included, and escaped first — otherwise a literal backslash in the
  // display is consumed by the unescape pass on the way back out.
  return display.replace(/([{}:\\])/g, "\\$1");
}

/**
 * Serialize a parsed reference back into the `{{type:display:id}}` token format.
 *
 * Round-trips with {@link parseNotificationContent}: parsing a token and then
 * serializing the result reproduces the original token (including escaping).
 *
 * @param reference - The parsed reference to serialize
 * @returns The `{{type:display:id}}` token string
 */
export function serializeReference(reference: Reference): string {
  return `{{${reference.type}:${escapeReferenceDisplay(reference.display)}:${reference.id}}}`;
}

/**
 * Render content as plain text by replacing references with their display text.
 *
 * @param content - The content string containing embedded references
 * @returns Plain text with references replaced by display text only
 */
export function renderPlainText(content: string | undefined | null): string {
  if (!content) {
    return "";
  }

  // Create a local regex with the g flag to replace all occurrences
  const pattern = new RegExp(REFERENCE_PATTERN.source, "g");

  // Replace tokens with their display text, then unescape the whole string in one
  // pass so escaped delimiters in free text render literally too — matching
  // render_plain_text on the backend.
  return unescapeDelimiters(
    content.replace(pattern, (_, _type, display) => display),
  );
}
